/**
 * Timezone Utilities
 * Centralized timezone detection and handling functions
 */

/**
 * Get the browser's current IANA timezone string
 * @returns {string} IANA timezone identifier (e.g., "America/Mazatlan")
 */
export const getBrowserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Map common/legacy IANA aliases to canonical values used by the app.
const TIMEZONE_ALIASES = {
  "Asia/Calcutta": "Asia/Kolkata",
};

/**
 * Normalize timezone aliases to canonical IANA values used in UI/options.
 * @param {string} timezone - Raw timezone value
 * @returns {string} Canonical timezone
 */
export const normalizeTimezone = (timezone) => {
  if (!timezone) return "";
  return TIMEZONE_ALIASES[timezone] || timezone;
};

/**
 * Get user's timezone from Redux store or localStorage
 * @param {Object} state - Redux global state (optional)
 * @returns {string|null} User's stored timezone or null if not set
 */
export const getUserTimezone = (state = null) => {
  // First try to get from state if provided
  if (state?.global?.userTimezone) {
    return normalizeTimezone(state.global.userTimezone);
  }
  
  // Fallback to localStorage
  const storedTimezone = localStorage.getItem("userTimezone");
  if (storedTimezone) {
    return normalizeTimezone(storedTimezone);
  }
  
  // Final fallback to browser timezone
  return normalizeTimezone(getBrowserTimezone());
};

/**
 * Initialize timezone on app startup
 * Restores timezone from localStorage or detects from browser
 * @returns {string} The timezone to use
 */
export const initializeTimezone = () => {
  const stored = localStorage.getItem("userTimezone");
  return normalizeTimezone(stored || getBrowserTimezone());
};

/**
 * Validate if a string is a valid IANA timezone
 * @param {string} timezone - Timezone string to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidTimezone = (timezone) => {
  if (!timezone) return false;
  
  try {
    // Try to format a date with the timezone
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Format timezone display string
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Formatted timezone for display
 */
export const formatTimezoneDisplay = (timezone) => {
  if (!timezone) return "Not set";
  return timezone.replace(/_/g, " ");
};

/**
 * Get current time in a specific timezone
 * @param {string} timezone - IANA timezone identifier
 * @returns {Date} Date object adjusted for the timezone
 */
export const getTimeInTimezone = (timezone = null) => {
  const tz = timezone || getBrowserTimezone();
  
  return new Date(
    new Date().toLocaleString("en-US", {
      timeZone: tz,
    })
  );
};

const FULL_TIMEZONE_OPTIONS = [
  { label: "US - Eastern (New York)", value: "America/New_York" },
  { label: "US - Central (Chicago)", value: "America/Chicago" },
  { label: "US - Mountain (Denver)", value: "America/Denver" },
  { label: "US - Pacific (Los Angeles)", value: "America/Los_Angeles" },
  { label: "US - Hawaii (Honolulu)", value: "Pacific/Honolulu" },
  { label: "Mexico - Central (Mexico City)", value: "America/Mexico_City" },
  { label: "Mexico - Mountain (Mazatlan)", value: "America/Mazatlan" },
  { label: "Mexico - Eastern (Cancun)", value: "America/Cancun" },
  { label: "India - Standard Time (Kolkata)", value: "Asia/Kolkata" },
];

const LIMITED_TIMEZONE_OPTIONS = [
  { label: "MST", value: "America/Mazatlan" },
  { label: "PST", value: "America/Los_Angeles" },
  { label: "IST", value: "Asia/Kolkata" },
];

const isLocalEnvironment = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".local")
  );
};

/**
 * Get user-friendly timezone label for a given timezone value
 * @param {string} timezoneValue - IANA timezone identifier (e.g., "Asia/Kolkata")
 * @returns {string} Friendly label (e.g., "India - Standard Time (Kolkata)" or "IST")
 */
export const getTimezoneLabelByValue = (timezoneValue) => {
  if (!timezoneValue) return "";

  const options = isLocalEnvironment() ? FULL_TIMEZONE_OPTIONS : LIMITED_TIMEZONE_OPTIONS;
  const found = options.find((option) => option.value === timezoneValue);
  return found ? found.label : timezoneValue;
};

export default {
  getBrowserTimezone,
  getUserTimezone,
  initializeTimezone,
  normalizeTimezone,
  isValidTimezone,
  formatTimezoneDisplay,
  getTimeInTimezone,
  getTimezoneLabelByValue,
};
